import { useEffect, useState } from "react";
import { LayoutDashboard, Package, ClipboardList, Truck, Users } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { cn } from "../../utils/cn";
import { InventoryOverview } from "./InventoryOverview";
import { InventoryArticles } from "./InventoryArticles";
import { InventoryRegistre } from "./InventoryRegistre";
import { InventoryCommandes } from "./InventoryCommandes";
import { InventoryFournisseurs } from "./InventoryFournisseurs";

export type InventorySubView = "overview" | "inventaire" | "article-detail" | "registre" | "commandes" | "fournisseurs";

function parseInventoryHash(hash: string): { view: InventorySubView; articleId: string | null; orderId: string | null; supplierId: string | null } {
  const path = hash.replace("#/inventories", "").replace(/\?.*$/, "") || "/";
  const segments = path.split("/").filter(Boolean);
  if (segments[0] === "inventaire" && segments[1]) {
    return { view: "inventaire", articleId: segments[1], orderId: null, supplierId: null };
  }
  if (segments[0] === "inventaire") return { view: "inventaire", articleId: null, orderId: null, supplierId: null };
  if (segments[0] === "registre") return { view: "registre", articleId: null, orderId: null, supplierId: null };
  if (segments[0] === "commandes" && segments[1]) {
    return { view: "commandes", articleId: null, orderId: segments[1], supplierId: null };
  }
  if (segments[0] === "commandes") return { view: "commandes", articleId: null, orderId: null, supplierId: null };
  if (segments[0] === "fournisseurs" && segments[1]) {
    return { view: "fournisseurs", articleId: null, orderId: null, supplierId: segments[1] };
  }
  if (segments[0] === "fournisseurs") return { view: "fournisseurs", articleId: null, orderId: null, supplierId: null };
  return { view: "overview", articleId: null, orderId: null, supplierId: null };
}

const NAV_ITEMS: { id: InventorySubView; label: string; href: string; icon: typeof Package }[] = [
  { id: "overview", label: "Vue d'ensemble", href: "#/inventories", icon: LayoutDashboard },
  { id: "inventaire", label: "Inventaire", href: "#/inventories/inventaire", icon: Package },
  { id: "registre", label: "Registre d'activité", href: "#/inventories/registre", icon: ClipboardList },
  { id: "commandes", label: "Commandes", href: "#/inventories/commandes", icon: Truck },
  { id: "fournisseurs", label: "Fournisseurs", href: "#/inventories/fournisseurs", icon: Users },
];

export function InventoryModulePage({ userName = "Utilisateur" }: { userName?: string }) {
  const [hash, setHash] = useState(() => window.location.hash);
  const { view, articleId, orderId, supplierId } = parseInventoryHash(hash);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const sidebar = (
    <nav className="flex flex-col gap-0.5 py-1 w-full max-w-[200px]" aria-label="Navigation Inventaire">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const active =
          (item.id === "inventaire" && (view === "inventaire" || view === "article-detail")) ||
          (item.id === "commandes" && view === "commandes") ||
          (item.id === "fournisseurs" && view === "fournisseurs") ||
          (item.id !== "inventaire" && item.id !== "commandes" && item.id !== "fournisseurs" && view === item.id);
        return (
          <a
            key={item.id}
            href={item.href}
            className={cn(
              "flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition border-l-2 -ml-px pl-2.5 min-w-0",
              active ? "bg-primary/10 text-primary font-medium border-primary" : "border-transparent text-muted hover:bg-surface-muted/70 hover:text-text"
            )}
          >
            <Icon size={16} className="shrink-0" aria-hidden />
            <span className="truncate">{item.label}</span>
          </a>
        );
      })}
    </nav>
  );

  let content: React.ReactNode;
  if (view === "overview") {
    content = <InventoryOverview />;
  } else if (view === "inventaire") {
    content = <InventoryArticles selectedArticleId={articleId} />;
  } else if (view === "registre") {
    content = <InventoryRegistre />;
  } else if (view === "commandes") {
    content = <InventoryCommandes selectedOrderId={orderId} />;
  } else if (view === "fournisseurs") {
    content = <InventoryFournisseurs selectedSupplierId={supplierId} />;
  } else {
    content = <InventoryOverview />;
  }

  return (
    <AppShell
      sidebar={sidebar}
      userName={userName}
      sidebarTitle="Inventaire"
      sidebarPinnedWidth={200}
      containerFluid={true}
      modules={[
        { label: "Tableau de bord", onClick: () => { window.location.hash = "#/"; } },
        { label: "Catalogue", onClick: () => { window.location.hash = "#/admin-products"; }, separator: true },
        { label: "Inventaire", onClick: () => { window.location.hash = "#/inventories"; }, sectionHeader: true },
      ]}
    >
      <div className="min-h-0 min-w-0 flex-1 overflow-auto bg-background p-6">
        {content}
      </div>
    </AppShell>
  );
}
